import {ref} from 'vue'
import Swal from 'sweetalert2'
import genericAPI from '@/api/api'
import {isValidJson} from "@/services/utility-service";

export function useAdminHandler() {
    const handlingInProgress = ref(false)

    enum CronType {
        users = "users",
        events = "events",
        networks = "networks",
        assignments = "assignments"
    }
    enum UserActionType {
        confirm = "confirm",
        disable = "disable",
        role = "role"
    }

    /**
     * Handle the AdminCron
     * @param type
     */
    const handleCron = async (type: CronType) => {
        try {
            handlingInProgress.value = true
            const result = await genericAPI.deleteDocument("admin/cron/", type)
            console.log(result)
            Swal.fire('Deletion of ' + type, `The deletion of ${type} has been successful and #${result.deletedCount} items were deleted.`,'success' )
        } catch (err) {
            console.error(err)
            Swal.fire('Error', 'An error occurred while deletion of ' + type, 'error')
        } finally {
            handlingInProgress.value = false
        }
    }

    const confirmCron = (type: CronType) => {
        Swal.fire({
            title: 'Start cronjob?',
            text: 'Are you sure you want to start delete ' + type + '?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'Cancel'
        }).then((result) => {
            if (result.isConfirmed) {
                return handleCron(type)
            }
        })
    }

    const confirmReset = () => {
        Swal.fire({
            title: 'Reset DB?',
            text: 'Are you sure you want to reset the DB?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, reset',
            cancelButtonText: 'Cancel'
        }).then((result) => {
            if (result.isConfirmed) {
                return handleReset()
            }
        })
    }

    const handleReset = async () => {
        try {
            handlingInProgress.value = true
            const result = await genericAPI.postDocument("admin/tools/resetDB", {})
            log.debug(result)
            Swal.fire(
                'DB Reset',
                `The reset of DB has been successful with this result:<br><br><div style="text-align:left">
<strong>Cleaned</strong><br><pre>${JSON.stringify(result?.cleanResult, null,2)}</pre>
<br><strong>Imported</strong><br><pre>${JSON.stringify(result?.importResult, null,2)}</pre>
</div>`,
                'success'
            )
        } catch (err) {
            console.error(err)
            Swal.fire('Error', 'An error occurred while resetting DB', 'error')
        } finally {
            handlingInProgress.value = false
        }
    }

    const confirmPush = () => {
        Swal.fire({
            title: 'Send push?',
            text: 'Are you sure you want to send push?',
            icon: 'question',
            input: "textarea",
            inputLabel: "Message",
            inputPlaceholder: "{\n\"title\":\"heyho\",\n\"body\": \"New update!\", \n\"icon\": \"/icon-192.png\"\n}",
            inputAttributes: {
                "aria-label": "Type your message here"
            },
            showCancelButton: true,
            inputValidator: (value) => {
                if (!value) {
                    return "You need to write something!";
                }
                if(!isValidJson(value)) {
                    return "The input is not a valid JSON object.";
                }
            },
            confirmButtonText: 'Yes, send',
            cancelButtonText: 'Cancel'
        }).then((result) => {
            if (result.isConfirmed) {
                const data = JSON.parse(result.value);
                return handlePush(data)
            }
        })
    }
    const handlePush = async (data: object) => {
        try {
            handlingInProgress.value = true
            const result = await genericAPI.postDocument("admin/tools/sendNotifications", data)
            Swal.fire(
                'Push Notification',
                `The push notifications are sent:<br><br> ${JSON.stringify(result, 2)}.`,
                'success'
            )
        } catch (err) {
            console.error(err)
            Swal.fire('Error', 'An error occurred while sending notifications', 'error')
        } finally {
            handlingInProgress.value = false
        }
    }

    const confirmUserAction = (action: UserActionType, userId: string, data: Record<string, any>) => {
        Swal.fire({
            title: `User action - ${action}`,
            text: 'Are you sure you want to ' + action + ' user +  ' + userId + '?',
            icon: 'question',
            input: "textarea",
            inputLabel: "Additional data (if required)",
            inputPlaceholder: action === UserActionType.role ? "{ \"role\": \"admin\" }" : "{ \"disabled\": true }",
            inputAttributes: {
                "aria-label": "Type additional data here"
            },
            showCancelButton: true,
            confirmButtonText: `Yes, ${action}`,
            cancelButtonText: 'Cancel'
        }).then((result) => {
            if (result.isConfirmed) {
                return handleUserAction(action, userId, data)
            }
        })
    }

    const handleUserAction = async (action: UserActionType, userId: string, data: Record<string, any>) => {
        try {
            log.debug(`handleUserAction(${action}, ${userId}, ${JSON.stringify(data)})`)
            if(!userId) {
                Swal.fire('Error', 'User ID is required for this action', 'error')
                return
            }
            if(action === UserActionType.confirm) {

            } else if(action === UserActionType.disable) {
                if(data.disabled === undefined) {
                    Swal.fire('Error', 'Disabled status is required for disabling/enabling user', 'error')
                    return
                }
            } else if(action === UserActionType.role) {
                if(!data || !data.role) {
                    Swal.fire('Error', 'Role is required for changing user role', 'error')
                    return
                }
            } else {
                Swal.fire('Error', 'Invalid user action type', 'error')
                return
            }

            handlingInProgress.value = true
            const result = await genericAPI.putDocument("admin/user/" + userId + "/" + action, data)

            Swal.fire(
                `User ${action}`,
                `Actions was sent:<br><br> ${JSON.stringify(result, 2)}.`,
                'success'
            )
        } catch (err) {
            console.error(err)
            Swal.fire('Error', 'An error occurred while sending userAction' , 'error')
        } finally {
            handlingInProgress.value = false
        }
    }


    return {
        handleCron,
        handleReset,
        confirmReset,
        confirmCron,
        confirmPush,
        confirmUserAction,
        handlingInProgress
    }
}
